const RATING_TYPES = new Set([
  "rating",
  "rating-scale",
  "rating-1-5",
  "rating-1-10",
  "1-5-rating",
  "1-10-rating",
  "scale-1-5",
  "scale-1-10",
]);

const FREE_TEXT_TYPES = new Set([
  "short-text",
  "long-text",
  "text",
  "textarea",
  "email",
  "phone",
]);

function clean(value) {
  return String(value ?? "").normalize("NFKC").trim();
}

function normaliseType(value) {
  const key = clean(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
  const aliases = {
    shorttext: "short-text",
    longtext: "long-text",
    textarea: "long-text",
    yesno: "yes-no",
    singlechoice: "single-choice",
    multiplechoice: "multiple-choice",
    multiselect: "multiple-choice",
    ratingscale: "rating-scale",
    rating15: "rating-1-5",
    rating1to5: "rating-1-5",
    rating110: "rating-1-10",
    rating1to10: "rating-1-10",
    scale15: "rating-1-5",
    scale110: "rating-1-10",
  };
  return aliases[key] || clean(value).toLowerCase().replace(/[\s_/]+/g, "-");
}

function isPersonalQuestion(question = {}) {
  const type = normaliseType(question.type);
  return question.personalData === true
    || question.isPersonalData === true
    || type === "email"
    || type === "phone";
}

export function timestampToDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function questionKey(question, index = 0) {
  return clean(question?.id || question?.questionId || question?.key || `question-${index + 1}`);
}

export function questionLabel(question, index = 0) {
  return clean(question?.label || question?.question || question?.title || `Question ${index + 1}`);
}

export function answerForQuestion(response, question, index = 0) {
  const key = questionKey(question, index);
  const answers = response?.answers && typeof response.answers === "object" ? response.answers : {};
  if (Object.prototype.hasOwnProperty.call(answers, key)) return answers[key];
  if (Object.prototype.hasOwnProperty.call(response || {}, key)) return response[key];
  return null;
}

function surveyVersion(record = {}) {
  const value = Number(record.surveyVersion ?? record.version ?? record.schemaVersion ?? 1);
  return Number.isInteger(value) && value > 0 ? value : 1;
}

function definitionFingerprint(question = {}) {
  return JSON.stringify({
    id: questionKey(question),
    label: questionLabel(question),
    type: normaliseType(question.type),
    required: question.required === true,
    personalData: isPersonalQuestion(question),
    min: Number.isFinite(Number(question.min)) ? Number(question.min) : null,
    max: Number.isFinite(Number(question.max)) ? Number(question.max) : null,
  });
}

export function buildSurveyQuestionSchemas(survey = {}, responses = []) {
  const definitionsByVersion = new Map();
  responses.forEach((response) => {
    const definitions = Array.isArray(response.questionDefinitions)
      ? response.questionDefinitions
      : [];
    if (definitions.length) {
      definitionsByVersion.set(surveyVersion(response), definitions);
    }
  });

  const currentQuestions = Array.isArray(survey.questions) ? survey.questions : [];
  const currentVersion = surveyVersion(survey);
  if (currentQuestions.length) definitionsByVersion.set(currentVersion, currentQuestions);

  const responseVersions = new Set(responses.map(surveyVersion));
  responseVersions.forEach((version) => {
    if (!definitionsByVersion.has(version) && currentQuestions.length) {
      definitionsByVersion.set(version, currentQuestions);
    }
  });

  const schemasByFingerprint = new Map();
  [...definitionsByVersion.entries()]
    .sort(([left], [right]) => left - right)
    .forEach(([version, definitions]) => {
      definitions.forEach((question, index) => {
        const fingerprint = definitionFingerprint(question);
        const existing = schemasByFingerprint.get(fingerprint);
        if (existing) {
          existing.versions.push(version);
          return;
        }
        schemasByFingerprint.set(fingerprint, {
          question,
          index,
          versions: [version],
          fingerprint,
        });
      });
    });

  const schemas = [...schemasByFingerprint.values()];
  const variantsById = schemas.reduce((counts, schema) => {
    const id = questionKey(schema.question, schema.index);
    counts[id] = (counts[id] || 0) + 1;
    return counts;
  }, {});
  return schemas.map((schema) => {
    const id = questionKey(schema.question, schema.index);
    const versionLabel = schema.versions.join(", ");
    return {
      ...schema,
      id: variantsById[id] > 1 ? `${id}-version-${versionLabel.replace(/\D+/g, "-")}` : id,
      label: variantsById[id] > 1
        ? `${questionLabel(schema.question, schema.index)} (survey version ${versionLabel})`
        : questionLabel(schema.question, schema.index),
    };
  });
}

function present(value) {
  if (Array.isArray(value)) return value.some((item) => clean(item));
  return value !== null && value !== undefined && clean(value) !== "";
}

function flattenedValues(value) {
  return (Array.isArray(value) ? value : [value]).map(clean).filter(Boolean);
}

function sortedCounts(values, denominator) {
  const counts = values.reduce((result, value) => {
    result[value] = (result[value] || 0) + 1;
    return result;
  }, {});
  return Object.entries(counts)
    .sort((first, second) => second[1] - first[1] || first[0].localeCompare(second[0]))
    .map(([label, count]) => ({
      label,
      count,
      percentage: denominator ? Math.round((count / denominator) * 1000) / 10 : 0,
    }));
}

function median(numbers) {
  if (!numbers.length) return null;
  const sorted = [...numbers].sort((first, second) => first - second);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

export function analyseSurveyQuestion(question, responses = [], index = 0) {
  const type = normaliseType(question?.type);
  const answeredRows = responses
    .map((response) => ({ response, value: answerForQuestion(response, question, index) }))
    .filter(({ value }) => present(value));
  const responded = answeredRows.length;
  const base = {
    id: questionKey(question, index),
    label: questionLabel(question, index),
    type,
    required: question?.required === true,
    personalData: isPersonalQuestion(question),
    responded,
    skipped: Math.max(0, responses.length - responded),
    responseRate: responses.length ? Math.round((responded / responses.length) * 1000) / 10 : 0,
  };

  if (RATING_TYPES.has(type)) {
    const ratings = answeredRows
      .map(({ value }) => Number(value))
      .filter(Number.isFinite);
    const distribution = sortedCounts(ratings.map(String), ratings.length)
      .sort((first, second) => Number(first.label) - Number(second.label));
    return {
      ...base,
      kind: "rating",
      average: ratings.length
        ? Math.round((ratings.reduce((sum, value) => sum + value, 0) / ratings.length) * 100) / 100
        : null,
      median: median(ratings),
      distribution,
      values: distribution,
    };
  }

  if (FREE_TEXT_TYPES.has(type)) {
    return {
      ...base,
      kind: "free-text",
      freeText: answeredRows.map(({ response, value }) => ({
        responseId: clean(response.responseId || response.id),
        submittedAt: response.submittedAt || response.createdAt || null,
        value: clean(value),
      })),
      values: [],
    };
  }

  const values = answeredRows.flatMap(({ value }) => flattenedValues(value));
  return {
    ...base,
    kind: "categorical",
    values: sortedCounts(values, responded),
  };
}

function dateKey(value) {
  const date = timestampToDate(value);
  return date ? date.toISOString().slice(0, 10) : "Unknown date";
}

function responseSurveyId(response) {
  return clean(response?.surveyId || response?.survey?.id);
}

function responseEventId(response) {
  return clean(response?.eventId || response?.event?.id);
}

function responseSource(response) {
  return clean(response?.submissionSource || response?.source) || "unknown";
}

function organisationNames(record) {
  const raw = record?.partnerOrganisations || record?.partners || record?.organisations || [];
  return (Array.isArray(raw) ? raw : [raw])
    .map((item) => clean(typeof item === "object" ? item.name || item.label : item))
    .filter(Boolean);
}

function responsePartners(response, surveys, events) {
  const direct = organisationNames(response);
  const survey = surveys.find((item) => clean(item.surveyId || item.id) === responseSurveyId(response));
  const event = events.find((item) => clean(item.eventId || item.id) === responseEventId(response));
  return [...new Set([
    ...direct,
    ...organisationNames(survey),
    ...organisationNames(event),
  ])];
}

function searchableResponseText(response) {
  const answers = response?.answers && typeof response.answers === "object"
    ? Object.values(response.answers)
    : [];
  return [
    response?.responseId,
    response?.surveyId,
    response?.eventId,
    responseSource(response),
    ...organisationNames(response),
    ...answers.flatMap((value) => Array.isArray(value) ? value : [value]),
  ].map(clean).join(" ").toLowerCase();
}

export function filterHousingResponses(responses = [], filters = {}, surveys = [], events = []) {
  const search = clean(filters.search).toLowerCase();
  const from = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`) : null;
  const to = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`) : null;
  const partner = clean(filters.partner).toLowerCase();
  return responses.filter((response) => {
    const submittedAt = timestampToDate(response.submittedAt || response.createdAt);
    const partners = responsePartners(response, surveys, events).map((name) => name.toLowerCase());
    return (!filters.surveyId || responseSurveyId(response) === filters.surveyId)
      && (!filters.eventId || responseEventId(response) === filters.eventId)
      && (!filters.source || responseSource(response) === filters.source)
      && (!filters.responseCategory || clean(response.responseCategory || response.category) === filters.responseCategory)
      && (!partner || partners.includes(partner))
      && (!from || (submittedAt && submittedAt >= from))
      && (!to || (submittedAt && submittedAt <= to))
      && (!search || searchableResponseText(response).includes(search));
  });
}

export function buildHousingSurveyReport(survey, responses = []) {
  const surveyId = clean(survey?.surveyId || survey?.id);
  const rows = surveyId
    ? responses.filter((response) => responseSurveyId(response) === surveyId)
    : responses;
  const schemas = buildSurveyQuestionSchemas(survey, rows);
  return {
    surveyId,
    surveyName: clean(survey?.surveyName || survey?.title),
    totalResponses: rows.length,
    responsesByDate: sortedCounts(rows.map((row) => dateKey(row.submittedAt || row.createdAt)), rows.length)
      .sort((first, second) => first.label.localeCompare(second.label)),
    responsesBySource: sortedCounts(rows.map(responseSource), rows.length),
    questions: schemas.map((schema) => {
      const versionRows = rows.filter((row) => schema.versions.includes(surveyVersion(row)));
      return {
        ...analyseSurveyQuestion(schema.question, versionRows, schema.index),
        id: schema.id,
        label: schema.label,
        surveyVersions: schema.versions,
      };
    }),
  };
}

// Kept as a small compatibility alias for callers that use the shorter name.
export const buildSurveyReport = buildHousingSurveyReport;

function dateOnly(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = timestampToDate(value);
  return date ? date.toISOString().slice(0, 10) : "";
}

export function getHousingDevelopmentMetrics(events = [], surveys = [], responses = [], now = new Date()) {
  const today = now.toISOString().slice(0, 10);
  const partnerNames = new Set([
    ...events.flatMap(organisationNames),
    ...surveys.flatMap(organisationNames),
  ].map((name) => name.toLowerCase()));
  const upcomingEvents = events.filter((event) => {
    const status = clean(event.status || event.lifecycleStatus).toLowerCase();
    const eventDate = dateOnly(event.date || event.startDate || event.eventDate);
    return eventDate >= today && !["cancelled", "completed", "archived"].includes(status);
  }).length;
  const activeSurveys = surveys.filter((survey) => {
    const status = clean(survey.status).toLowerCase();
    return survey.published === true || survey.active === true || status === "published";
  }).length;
  const attendance = events.reduce((total, event) => (
    total + Number(event.attendanceCount || event.attended || event.metrics?.attendance || 0)
  ), 0);
  return {
    upcomingEvents,
    totalEvents: events.length,
    activeSurveys,
    totalSurveyResponses: responses.length,
    eventAttendance: attendance,
    partnerOrganisations: partnerNames.size,
  };
}

export function getPartnerReport(partnerName, events = [], surveys = [], responses = []) {
  const partner = clean(partnerName);
  const matches = (record) => organisationNames(record)
    .some((name) => name.toLowerCase() === partner.toLowerCase());
  const partnerEvents = events.filter(matches);
  const partnerSurveys = surveys.filter(matches);
  const surveyIds = new Set(partnerSurveys.map((item) => clean(item.surveyId || item.id)));
  const eventIds = new Set(partnerEvents.map((item) => clean(item.eventId || item.id)));
  const partnerResponses = responses.filter((response) => (
    surveyIds.has(responseSurveyId(response))
      || eventIds.has(responseEventId(response))
      || responsePartners(response, surveys, events)
        .some((name) => name.toLowerCase() === partner.toLowerCase())
  ));
  return {
    partner,
    events: partnerEvents,
    surveys: partnerSurveys,
    responses: partnerResponses,
    responseCount: partnerResponses.length,
  };
}

export function listPartnerNames(events = [], surveys = []) {
  return [...new Set([...events.flatMap(organisationNames), ...surveys.flatMap(organisationNames)])]
    .sort((first, second) => first.localeCompare(second));
}

export function getPublishedSurveysForEvent(event = {}, surveys = []) {
  const eventId = clean(event.eventId || event.id);
  if (!eventId) return [];
  return surveys.filter((survey) => {
    const status = clean(survey.status).toLowerCase();
    const published = survey.published === true || status === "published";
    const eventIds = Array.isArray(survey.eventIds)
      ? survey.eventIds.map(clean).filter(Boolean)
      : [];
    const primaryEventId = clean(survey.eventId);
    return published && (primaryEventId === eventId || eventIds.includes(eventId));
  });
}

export function formatAdminDate(value) {
  const date = timestampToDate(value);
  if (!date) return "Date unavailable";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/London",
  }).format(date);
}

